/**
 * THE RESULT SHEET.
 *
 * Straight off `RaceResult` — finishing order, lead changes, cautions. D-EZ-102:
 * it now opens as a story — one paragraph built from the same facts (the winner
 * and how, the cautions with the Big One named, the lead changes, the notable
 * cars that did not finish) — and says plainly when the rain shortened it. No
 * number here is recomputed.
 */

import type { RaceResult } from '../domain/race.ts';
import { raceStoryParagraph } from '../domain/story/raceStory.ts';
import { ordinal } from './boardModel.ts';
import { Cite } from './Cite.tsx';

export interface RaceResultsProps {
  result: RaceResult;
}

const OUT_REASON: Record<string, string> = {
  trouble: 'wrecked',
  problem: 'mechanical',
  feud: 'feud',
  fuel: 'out of fuel',
  'black-flag': 'black flag',
  'repair-failed': 'repair failed',
  injury: 'hurt',
};

export default function RaceResults({ result }: RaceResultsProps) {
  const nameOf = (id: string) =>
    result.finishingOrder.find((f) => f.driverId === id)?.name ?? id;
  const called = result.called ?? null;

  return (
    <section className="results" aria-label="Race result">
      <header className="results__head">
        <h2>{called?.reason === 'rain' ? 'Rain-shortened' : called ? 'Race called' : 'Checkered'}</h2>
        <p className="results__story">{raceStoryParagraph(result)}</p>
      </header>

      <div className="results__cols">
        <div className="results__order">
          <h3>Finishing order</h3>
          <ol className="order">
            {result.finishingOrder.map((f) => (
              <li key={f.driverId} className={f.status === 'out' ? 'is-out' : ''}>
                <span className="order__place">{ordinal(f.place)}</span>
                <span className="order__name">{f.name}</span>
                <span className="order__note">
                  {f.status === 'out'
                    ? `out turn ${f.outTurn} · ${OUT_REASON[f.outReason ?? ''] ?? f.outReason}`
                    : f.turnsLed > 0
                      ? `led ${f.turnsLed} turn${f.turnsLed === 1 ? '' : 's'}`
                      : ''}
                </span>
                {f.chips > 0 && <span className="order__chips">{f.chips} chip</span>}
              </li>
            ))}
          </ol>
        </div>

        <div className="results__side">
          <section>
            <h3>Lead changes · {result.leadChanges.length}</h3>
            <ul className="tick">
              {result.leadChanges.map((lc, i) => (
                <li key={i}>
                  <span className="tick__t">T{lc.turn}</span> {nameOf(lc.driverId)}
                </li>
              ))}
              {result.leadChanges.length === 0 && <li className="dim">Nobody was headed.</li>}
            </ul>
          </section>

          <section>
            <h3>Cautions · {result.cautions.length}</h3>
            <ul className="tick">
              {result.cautions.map((c, i) => (
                <li key={i}>
                  <span className="tick__t">T{c.turn}</span> {c.cars.length} car
                  {c.cars.length === 1 ? '' : 's'} collected · {c.turns} yellow turn
                  {c.turns === 1 ? '' : 's'}
                </li>
              ))}
              {result.cautions.length === 0 && <li className="dim">Green to green.</li>}
            </ul>
          </section>

          <section>
            <h3>Distance</h3>
            <ul className="tick">
              <li>
                <span className="tick__t">Sched</span> {result.scheduledTurns} turns
              </li>
              <li>
                <span className="tick__t">Run</span> {result.turnsRun} turns
                {called ? (
                  <em className="dim">
                    {' '}·{' '}
                    {called.reason === 'rain'
                      ? `rain-shortened at turn ${called.turn} of ${called.scheduledTurns}`
                      : `called at turn ${called.turn} of ${called.scheduledTurns}`}
                    <Cite> ({called.ruleCitation})</Cite>
                  </em>
                ) : (
                  result.turnsRun > result.scheduledTurns && (
                    <em className="dim">
                      {' '}· a race cannot end under yellow<Cite> (RB/xv)</Cite>
                    </em>
                  )
                )}
              </li>
            </ul>
          </section>
        </div>
      </div>
    </section>
  );
}
